import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../../app/helpers";
import { RootState } from "../../../../app/rootReducer";
import { FloorPlanMemo } from "../../../../features/Reports/typings/reports.types";
import { FilterTypState } from "./facility-report-filter-and-selector.component";
import { applyBookingFilterOnFloorPlan } from "../../../BookingForm/functions/form.functions";
import {
  GroupFilterSelected,
  GroupFilterType
} from "../../../BookingForm/BookingFilter/types/GroupFilters.type";
import { BookingFilterMode } from "../../../BookingForm/Domain/booking-filter-mode-enum.type";
import { GroupFilters } from "../../../BookingForm/BookingFilter/GroupFilters.component";
import { findAttributesBySelection } from "./functions/findAttributeBySelection.function";
import { BookingType } from "../../../../features/Booking-Form/typings/booking-inputs";
import { IFloorPayload } from "../../../FacilityManager/Domain/Types/FloorPlan/FloorPayload.type";
import { FilteredSelectables } from "../FilterSlideout/FilterSlideoutDialog.component";
import * as _ from "lodash";

type P = {
  floorData: FloorPlanMemo;
  filterType: FilterTypState[];
  filteredSelectables: FilteredSelectables;
  setFilteredSelectables: Dispatch<SetStateAction<FilteredSelectables>>;
  setAttributeSelected: (sel: GroupFilterSelected[]) => void;
};

export function AttributeSelector({
  floorData,
  filterType,
  setFilteredSelectables,
  setAttributeSelected
}: Readonly<P>) {
  const { t } = useTranslation();

  const {
    currentFloors,
    selectedZoneOrWorkplace,
    selectedBookingType,
    filterSelected: { selectedAttributes }
  } = useSelector((state: RootState) => state.report);

  const options = findAttributesBySelection({
    floorData,
    filterType,
    currentFloors,
    selectedZoneOrWorkplace,
    selectedBookingType
  });

  const isNoOptionAvailable =
    !options.bookingProperties.length &&
    !options.categories.length &&
    !options.equipment.length &&
    !options.equipmentCategories.length;

  const [attFilterItems, setAttFilterItems] = useState<GroupFilterSelected[]>([]);
  const [isLoading, setIsLoading] = useState<boolean | null>(null);

  useEffect(() => {
    if (attFilterItems.length > 0) {
      // first floor
      const floorPlanData = floorData[currentFloors[0].id].floorData;
      handleApplyFilterOnFloorPlan({
        floorInventoryId: currentFloors[0].id,
        attFilterItems,
        selectedBookingType,
        floorData: floorPlanData,
        setFilteredSelectables
      });

      // second floor (only when array has at least two floors)
      if (currentFloors.length > 1) {
        const secondFloor = currentFloors[1];
        if (secondFloor?.open) {
          const floorPlanData = floorData[secondFloor.id].floorData;
          handleApplyFilterOnFloorPlan({
            floorInventoryId: secondFloor.id,
            attFilterItems,
            selectedBookingType,
            floorData: floorPlanData,
            setFilteredSelectables
          });
        }
      }

      setAttributeSelected(attFilterItems);
    } else {
      // if none filter items are selected then reset
      setFilteredSelectables({});
      setAttributeSelected([]);
    }
  }, [attFilterItems]);

  useEffect(() => {
    if (!selectedAttributes?.length) setIsLoading(true);

    // apply the initial selected attribute filter items
    setAttFilterItems(
      _.uniqWith(
        selectedAttributes,
        (extA, extB) => extA.id === extB.id && extA.type === extB.type && extA.name === extB.name
      )
    );
    setIsLoading(false);
  }, []);

  return (
    <>
      {isLoading === false && (
        <GroupFilters
          floorPlan={floorData[currentFloors[0].id].floorData} // only to check if floorPlan exists or not
          showDetailGroupFilter={false}
          filterItems={attFilterItems}
          setFilterItems={setAttFilterItems}
          usedCategory={{ type: options.categoryType, options: options.categories }}
          usedOptions={{
            usedEquipment: options.equipment,
            usedEquipmentCategories: options.equipmentCategories,
            usedBookingProperties: options.bookingProperties
          }}
          filterMode={BookingFilterMode.FILTERING}
          isCapacityReportUsed
        />
      )}

      {(isLoading || isNoOptionAvailable) && (
        <Box sx={{ p: 1 }}>
          <Typography>{t("No {{title}} to display", { title: t("Attributes") })}</Typography>
        </Box>
      )}
    </>
  );
}

export const handleApplyFilterOnFloorPlan = ({
  floorInventoryId,
  attFilterItems,
  selectedBookingType,
  floorData,
  setFilteredSelectables
}: {
  floorInventoryId: number;
  attFilterItems: GroupFilterSelected[];
  selectedBookingType: BookingType | null;
  floorData: IFloorPayload;
  setFilteredSelectables: Dispatch<SetStateAction<FilteredSelectables>>;
}) => {
  const equipCate = attFilterItems
    .filter(item => item.type === GroupFilterType.EQUIPMENTCATEGORY)
    .map(i => ({ id: i.id, quantity: i.quantity }));
  const equipment = attFilterItems
    .filter(item => item.type === GroupFilterType.EQUIPMENT)
    .map(i => i.id);
  const bookProp = attFilterItems
    .filter(item => item.type === GroupFilterType.BOOKINGPROPERTY)
    .map(i => i.id);
  const placeCate = attFilterItems
    .filter(item => item.type === GroupFilterType.PLACECATEGORY)
    .map(i => i.id);
  const zoneCate = attFilterItems
    .filter(item => item.type === GroupFilterType.ZONECATEGORY)
    .map(i => i.id);

  // re-use the same FN from the booking process to find the entries from the floor plan
  applyBookingFilterOnFloorPlan(
    {
      mode: selectedBookingType,
      bookingFilters: {
        equipmentIds: equipment,
        equipmentCategories: equipCate,
        propertyIds: bookProp,
        placeCategoryIds: placeCate,
        zoneCategoryIds: zoneCate
      }
    } as any,
    floorData,
    attFilterItems,
    BookingFilterMode.FILTERING,
    undefined, // setFilteredSelectables : not using in the report module
    entries =>
      /* istanbul ignore next */
      setFilteredSelectables(prev => ({
        ...prev,
        [floorInventoryId]: {
          floorInventoryId: floorInventoryId,
          entries: entries
        }
      }))
  );
};
